import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { toast } from '@/lib/notifications';
import { setTenantDemoFlagCallable } from '@/lib/callables';
import type { TenantId } from '@etiquetachef/types';

type Props = {
  open: boolean;
  tenantId: TenantId;
  tenantName: string;
  /** Valor que será gravado ao confirmar (o oposto do estado atual). */
  nextValue: boolean;
  onClose: () => void;
};

/**
 * Confirmação de marcar/desmarcar tenant como demonstração. Não é destrutivo,
 * mas some com o tenant dos relatórios internos de negócio — marcar um cliente
 * real por engano o esconde do radar de churn sem nenhum sinal na UI dele.
 *
 * Origem: #275 — commit 6ad012b60, 2026-08-26 20:20 +0200.
 *
 * Contrato: chama `setTenantDemoFlag` com `{ tenantId, isDemo: nextValue }`.
 * Desde a #738 (#679 F7) o callable mexe em duas marcas juntas:
 * `tenants/{id}.isDemo` e `billing/subscription.commercial_mode: DEMO` (libera
 * o paywall e tira o tenant do MRR); desligar um demo que ainda tem assinatura
 * no gateway a cancela. Marcar um cliente real por engano, portanto, também
 * deixa de cobrá-lo. Erro vira `Alert` dentro do modal (#1021 — o toast ficava
 * atrás do overlay).
 *
 * Usado por: `routes/TenantDetailPage.tsx` (interruptor de demo no cabeçalho).
 *
 * Arquitetura: só super-admin escreve o campo (`firestore.rules` bloqueia até
 * o OWNER); regra de cada lado em `planDemoFlag`
 * (`apps/functions/src/lib/billing/courtesy.ts`).
 *
 * Issues: #275 (interruptor de demo), #738 (demo como modo comercial), #795
 * (ADM-7: exige capability ROOT/FINANCE), #1021 (erro dentro do modal; #174).
 *
 * Histórico:
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function DemoFlagDialog({
  open,
  tenantId,
  tenantName,
  nextValue,
  onClose,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close(): void {
    setError(null);
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await setTenantDemoFlagCallable({ tenantId, isDemo: nextValue });
      toast.success(
        nextValue
          ? t('tenantDetail.demoFlag.markedDemo')
          : t('tenantDetail.demoFlag.markedReal'),
      );
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={
        nextValue
          ? t('tenantDetail.demoFlag.confirmMarkTitle')
          : t('tenantDetail.demoFlag.confirmUnmarkTitle')
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              loading={submitting}
            >
              {t('tenantDetail.demoFlag.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {nextValue
            ? t('tenantDetail.demoFlag.confirmMarkDescription', { name: tenantName })
            : t('tenantDetail.demoFlag.confirmUnmarkDescription', { name: tenantName })}
        </Box>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
